import { waitMs } from "@src/shared/utils/async/waitMs";
import { captureMessage } from "@src/shared/utils/observability/sentry";

import { authClient } from "../data";

const DEFAULT_ATTEMPTS = 10;
const DEFAULT_DELAY_MS = 150;

/**
 * Better Auth's `getSession` and `useSession` share one reactive session
 * store. Right after `signUp.email()` the store can briefly lag behind the
 * just-set session cookie, so a protected route that mounts immediately reads
 * `session === null`. Polling `getSession` (cache-busted) both confirms the
 * session is established and primes the store, so the next render of
 * `useSession()` resolves with data.
 *
 * Resolves `true` once a session exists; `false` if attempts are exhausted
 * first (callers should proceed anyway — ActorProvider is the safety net).
 */
export async function waitForSession({
  attempts = DEFAULT_ATTEMPTS,
  delayMs = DEFAULT_DELAY_MS,
  reportOnExhaustion = true,
}: {
  attempts?: number;
  delayMs?: number;
  reportOnExhaustion?: boolean;
} = {}): Promise<boolean> {
  let lastProbeError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const { data } = await authClient.getSession({
        query: { disableCookieCache: true },
      });

      if (data?.session) {
        return true;
      }
    } catch (error) {
      // A failed probe (e.g. transient network error) is treated the same as
      // "session not ready yet" — keep polling. Callers proceed on a false
      // return; ActorProvider is the safety net. We retain the error so a
      // *persistent* failure (revoked token, 5xx, contract drift) surfaces in
      // the exhaustion report below instead of looking like a benign race.
      lastProbeError = error;
    }

    if (attempt < attempts - 1) {
      await waitMs(delayMs);
    }
  }

  if (!reportOnExhaustion) return false;

  // Exhaustion is the STITCH-426 pathology — the session never primed within
  // budget. Surface it (callers still proceed) so the window is tunable from
  // real data and a chronic auth failure is distinguishable from a slow race.
  captureMessage("[waitForSession] session not ready within budget", {
    level: "warning",
    extra: {
      attempts,
      delayMs,
      lastProbeError:
        lastProbeError instanceof Error ? lastProbeError.message : undefined,
    },
  });

  return false;
}
